import { Link } from "react-router-dom";
import { ArrowRight, Bluetooth, Radio, Network, Cpu, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import CTAStrip from "@/components/site/CTAStrip";
import { useSEO, useJsonLd, SITE_URL, breadcrumbSchema } from "@/lib/seo";
import { SpecTable, Prose, ReviewStamp } from "@/components/site/ContentPrimitives";
import { GoAndTrackLink } from "@/lib/goAndTrack";

export default function BluetoothSmartLabels() {
  useSEO({
    title: "Bluetooth Smart Labels | BLE Package Tracking Devices",
    description: "Bluetooth (BLE) disposable smart labels for shipment visibility — low-power, infrastructure-light package tracking devices that fit existing logistics ecosystems.",
    canonical: "/bluetooth-smart-labels",
  });

  useJsonLd(
    "breadcrumb-ble",
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Disposable Package Tracker", url: "/disposable-package-tracker" },
      { name: "Bluetooth Smart Labels" },
    ]),
  );

  return (
    <div data-testid="page-bluetooth">
      <PageHero
        eyebrow="Technology"
        title="Bluetooth Smart Labels"
        subtitle="Low-power BLE smart labels in a disposable form factor — infrastructure-light shipment visibility that fits into existing logistics ecosystems."
      >
        <Link to="/request-a-trial" className="btn-primary" data-testid="page-cta-trial">
          Speak to an expert <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHero>

      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-4">What it is</p>
            <h2 className="h2">A disposable label powered by BLE.</h2>
          </div>
          <div className="lg:col-span-7 space-y-5">
            <p className="body-text">
              Bluetooth Low Energy (BLE) is a power-efficient wireless protocol
              widely supported across mobile devices, gateways, and logistics
              infrastructure. Our Bluetooth smart labels apply
              that protocol in a single-use, peel-and-stick format — purpose-built
              for shipments rather than long-life hardware estates.
            </p>
            <p className="body-text">
              Many buyers arrive looking for a{" "}
              <Link to="/disposable-gps-tracker" className="text-brand hover:underline">
                disposable GPS tracker
              </Link>
              {" "}or a{" "}
              <Link to="/gps-tracker-for-packages" className="text-brand hover:underline">
                GPS tracker for packages
              </Link>
              . In practice, most modern disposable shipment tracking devices
              use BLE rather than cellular GPS — delivering the same operational
              outcome (location, dwell, exceptions) in a thinner, lower-cost,
              single-use form factor.
            </p>
            <p className="body-text">
              The result: a thin, disposable label that delivers location, dwell,
              and exception data into <GoAndTrackLink />, without standing up dedicated
              tracking infrastructure on every site.
            </p>
          </div>
        </div>
      </section>

      <section className="section bg-surface-subtle border-y border-line">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
            {[
              { icon: Bluetooth, k: "BLE protocol", v: "Industry-standard, low-power, widely supported across logistics ecosystems." },
              { icon: Radio, k: "Light infrastructure footprint", v: "Works with mobile, gateway, and partner-network detection points." },
              { icon: Network, k: "Multi-leg compatibility", v: "Designed to provide useful data across handovers and modal changes." },
              { icon: Cpu, k: "Disposable form factor", v: "Engineered for one shipment journey — no recharging, pairing, or recovery required." },
            ].map((b) => (
              <div key={b.k} className="bg-white p-7 lg:p-8">
                <b.icon className="h-5 w-5 text-brand" />
                <h3 className="h3 mt-4">{b.k}</h3>
                <p className="body-text mt-2 text-sm">{b.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <p className="eyebrow mb-4">Why BLE for disposable</p>
            <h2 className="h2">A protocol that suits the form factor.</h2>
            <ul className="mt-8 space-y-3.5">
              {[
                "Low energy budget extends label life across the shipment journey.",
                "Mature, well-supported protocol used widely in logistics infrastructure.",
                "Integrates cleanly into existing gateway, mobile, and partner detection setups.",
                "Sized appropriately for cartons, parcels, and pallets without bulky enclosures.",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px] text-ink">
                  <CheckCircle2 className="h-4 w-4 mt-1 text-brand shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="border border-line bg-white p-8 lg:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-subtle">
                Related
              </p>
              <h3 className="h3 mt-3">Continue exploring</h3>
              <div className="mt-5 divide-y divide-line">
                {[
                  { to: "/disposable-smart-labels", label: "Disposable Smart Labels — overview" },
                  { to: "/disposable-vs-reusable-trackers", label: "Disposable vs Reusable comparison" },
                  { to: "/smart-labels-for-logistics", label: "Smart Labels for Logistics" },
                  { to: "/smart-labels-for-pilot-projects", label: "Smart Labels for Pilot Projects" },
                ].map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="flex items-center justify-between py-4 text-sm font-medium text-ink hover:text-brand transition-colors"
                  >
                    {l.label} <ArrowRight className="h-4 w-4" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section border-t border-line">
        <div className="container-x">
          <Prose>
            <h2 className="h2">Reference specification — ultra-thin BLE label</h2>
            <p className="body-text mt-3">
              Representative specifications for the ultra-thin BLE
              disposable label class deployed by the programme:
            </p>
            <SpecTable
              title="Ultra-thin BLE disposable smart label"
              rows={[
                { k: "Wireless", v: "Bluetooth 5.3" },
                { k: "Dimensions", v: "65 × 55 mm" },
                { k: "Thickness", v: "0.4 – 1.2 mm" },
                { k: "Weight", v: "Approximately 4 g" },
                { k: "Battery", v: "Zinc-manganese, non-replaceable" },
                { k: "Battery life", v: "Up to 40 days (reporting-dependent)" },
                { k: "Range", v: "Up to 70 m BLE open-area" },
                { k: "Activation", v: "Peel / cut pull-tab" },
                { k: "Certifications", v: "CE, FCC, RoHS" },
              ]}
              note="Air-freight suitability depends on the specific device, battery chemistry, airline, and route. Validate the selected device for the operating lane."
            />

            <h2 className="h2 mt-14">Responsible disposal</h2>
            <p className="body-text mt-3">
              BLE smart labels contain electronic components and a
              non-replaceable battery. End-of-life treatment depends on
              product construction, battery chemistry, and local
              e-waste / battery-waste requirements. Follow the
              device-specific disposal route rather than placing used
              labels in general waste.
            </p>

            <ReviewStamp
              date="February 2026"
              basis="Manufacturer specifications for the deployed BLE label class and direct deployment experience across UK/EU logistics lanes."
            />
          </Prose>
        </div>
      </section>

      <CTAStrip />
    </div>
  );
}
